import {
  ensureGoogleMapsLoaded,
  googleGeocodingIsLoaded,
  googleMapsIsLoaded,
  googlePlacesIsLoaded,
} from './google-maps-loader.util';

const CACHE_TTL_MS = 10 * 60 * 1000;
const CACHE_LIMIT = 100;
const REQUEST_TIMEOUT_MS = 8000;

let sharedGeocoder: google.maps.Geocoder | undefined;
let sharedAutocompleteService:
  | google.maps.places.AutocompleteService
  | undefined;

interface PlaceGeocodeCacheEntry {
  expiresAt: number;
  promise: Promise<google.maps.GeocoderResult | undefined>;
}

const placeGeocodeCache = new Map<string, PlaceGeocodeCacheEntry>();

/**
 * Both getters answer `undefined` when the Maps SDK is not loaded, and they
 * never throw. They used to build their service straight off the global, so
 * the city screen threw on opening when the script had not arrived, and then
 * threw again on every keystroke: CAPACITOR-K and CAPACITOR-J, 43 events in
 * one minute for one member on 20 September 2026. See google-maps-loader.
 */
export function getSharedGoogleGeocoder(): google.maps.Geocoder | undefined {
  if (!googleGeocodingIsLoaded()) {
    return undefined;
  }
  sharedGeocoder ||= new google.maps.Geocoder();
  return sharedGeocoder;
}

export function getSharedGoogleAutocompleteService():
  | google.maps.places.AutocompleteService
  | undefined {
  if (!googlePlacesIsLoaded()) {
    return undefined;
  }
  sharedAutocompleteService ||= new google.maps.places.AutocompleteService();
  return sharedAutocompleteService;
}

/**
 * The autocomplete service, asking for the SDK when it is not there yet.
 *
 * This is what a screen calls. It answers `undefined` rather than throwing,
 * and it never keeps a stale answer: a member who opened the application with
 * no network and reaches this screen ten minutes later gets a working search.
 */
export async function autocompleteServiceWhenReady(): Promise<
  google.maps.places.AutocompleteService | undefined
> {
  const here = getSharedGoogleAutocompleteService();
  if (here) {
    return here;
  }
  return (await ensureGoogleMapsLoaded()) ? getSharedGoogleAutocompleteService() : undefined;
}

export function preloadGoogleLocationServices(): void {
  if (!googleMapsIsLoaded()) {
    return;
  }
  getSharedGoogleAutocompleteService();
  getSharedGoogleGeocoder();
  if (typeof google.maps.importLibrary === 'function') {
    void Promise.all([
      google.maps.importLibrary('places'),
      google.maps.importLibrary('geocoding'),
    ]).catch(() => undefined);
  }
}

function removeOldestEntry(): void {
  if (placeGeocodeCache.size < CACHE_LIMIT) {
    return;
  }
  const oldestKey = placeGeocodeCache.keys().next().value as
    | string
    | undefined;
  if (oldestKey) {
    placeGeocodeCache.delete(oldestKey);
  }
}

/**
 * Resolve a prediction directly by placeId and share both completed and
 * in-flight requests between autocomplete enrichment and selection.
 */
export function getCachedPlaceGeocode(
  placeId: string,
  language: string,
): Promise<google.maps.GeocoderResult | undefined> {
  const key = `${placeId}:${language}`;
  const now = Date.now();
  const existing = placeGeocodeCache.get(key);
  if (existing && existing.expiresAt > now) {
    return existing.promise;
  }
  if (existing) {
    placeGeocodeCache.delete(key);
  }

  removeOldestEntry();
  const promise = new Promise<google.maps.GeocoderResult | undefined>(
    (resolve) => {
      let settled = false;
      const geocoder = getSharedGoogleGeocoder();
      if (!geocoder) {
        // No SDK, no geocoding. The caller gets the same answer it gets for
        // a place the geocoder does not know, which it already handles.
        resolve(undefined);
        return;
      }
      const timeout = setTimeout(() => {
        if (!settled) {
          settled = true;
          resolve(undefined);
        }
      }, REQUEST_TIMEOUT_MS);

      geocoder.geocode(
        {
          placeId,
          language,
        },
        (results, status) => {
          if (settled) {
            return;
          }
          settled = true;
          clearTimeout(timeout);
          if (status !== 'OK' || !results?.length) {
            resolve(undefined);
            return;
          }
          resolve(
            results.find((result) => result.place_id === placeId) ||
              results[0],
          );
        },
      );
    },
  );

  placeGeocodeCache.set(key, {
    expiresAt: now + CACHE_TTL_MS,
    promise,
  });
  void promise.then((result) => {
    if (!result && placeGeocodeCache.get(key)?.promise === promise) {
      placeGeocodeCache.delete(key);
    }
  });
  return promise;
}
